import type {
  ComponentPropsWithoutRef,
  HTMLAttributes,
  ReactNode,
} from "react";
import "./OperationalPageLayout.css";

export type OperationalPageFrameSize = "standard" | "compact" | "wide";

export type OperationalPageFrameProps = Omit<
  ComponentPropsWithoutRef<"main">,
  "children"
> & {
  children: ReactNode;
  size?: OperationalPageFrameSize;
  as?: "main" | "section" | "div";
};

export function OperationalPageFrame({
  children,
  size = "standard",
  as = "main",
  className,
  ...props
}: OperationalPageFrameProps) {
  const Frame = as;

  return (
    <Frame
      {...props}
      className={[
        "operational-page-shell",
        "operational-page-shell--" + size,
        "operational-page-frame",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-page-size={size}
    >
      {children}
    </Frame>
  );
}

export type OperationalPageHeaderProps = Omit<
  HTMLAttributes<HTMLElement>,
  "children"
> & {
  title: ReactNode;
  titleId?: string;
  eyebrow?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
};

export function OperationalPageHeader({
  title,
  titleId,
  eyebrow,
  description,
  actions,
  className,
  ...props
}: OperationalPageHeaderProps) {
  return (
    <header
      {...props}
      className={["operational-page-header", className]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="operational-page-header__copy">
        {eyebrow ? (
          <p className="operational-page-header__eyebrow">{eyebrow}</p>
        ) : null}
        <h1 id={titleId} className="operational-page-header__title">
          {title}
        </h1>
        {description ? (
          <p className="operational-page-header__description">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div
          className="operational-page-header__actions"
          role="group"
          aria-label="Ações da página"
        >
          {actions}
        </div>
      ) : null}
    </header>
  );
}

export type OperationalToolbarProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  search?: ReactNode;
  filters?: ReactNode;
  actions?: ReactNode;
  status?: ReactNode;
  children?: ReactNode;
  ariaLabel?: string;
};

export function OperationalToolbar({
  search,
  filters,
  actions,
  status,
  children,
  ariaLabel = "Ferramentas da página",
  className,
  ...props
}: OperationalToolbarProps) {
  return (
    <div
      {...props}
      className={["operational-toolbar", className].filter(Boolean).join(" ")}
      role="group"
      aria-label={ariaLabel}
    >
      {search ? (
        <div className="operational-toolbar__search">{search}</div>
      ) : null}
      {filters ? (
        <div className="operational-toolbar__filters">{filters}</div>
      ) : null}
      {actions ? (
        <div className="operational-toolbar__actions">{actions}</div>
      ) : null}
      {children}
      {status ? (
        <div className="operational-toolbar__status">{status}</div>
      ) : null}
    </div>
  );
}
